import logger from '@utils/logger'
import { isPrivacyCategoryAllowed } from '@/privacy/privacyPreferences'
/**
 * Simple cache utility using localStorage
 * Provides time-based cache invalidation
 */

const CACHE_PREFIX = 'x3algo_blog_cache_';
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Cache utility for storing and retrieving data with TTL
 */
export const cache = {
  /**
   * Set a value in cache with TTL
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttl - Time to live in milliseconds (default: 5 minutes)
   */
  set(key, value, ttl = DEFAULT_TTL) {
    if (!isPrivacyCategoryAllowed('experience')) return

    try {
      const cacheKey = CACHE_PREFIX + key;
      const cacheData = {
        value,
        timestamp: Date.now(),
        ttl
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      logger.warn('Failed to set cache:', error);
    }
  },

  /**
   * Get a value from cache if not expired
   * @param {string} key - Cache key
   * @returns {any|null} Cached value or null if expired/not found
   */
  get(key) {
    if (!isPrivacyCategoryAllowed('experience')) return null

    try {
      const cacheKey = CACHE_PREFIX + key;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        return null;
      }

      const cacheData = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - cacheData.timestamp > cacheData.ttl) {
        this.remove(key);
        return null;
      }

      return cacheData.value;
    } catch (error) {
      logger.warn('Failed to get cache:', error);
      return null;
    }
  },

  /**
   * Remove a value from cache
   * @param {string} key - Cache key
   */
  remove(key) {
    try {
      const cacheKey = CACHE_PREFIX + key;
      localStorage.removeItem(cacheKey);
    } catch (error) {
      logger.warn('Failed to remove cache:', error);
    }
  },

  /**
   * Clear all cache entries
   */
  clear() {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      logger.warn('Failed to clear cache:', error);
    }
  },

  /**
   * Check if a cache entry exists and is valid
   * @param {string} key - Cache key
   * @returns {boolean} True if cache exists and is valid
   */
  has(key) {
    return this.get(key) !== null;
  }
};

export default cache;
